import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** Maximum token-refresh attempts before the session is forcibly expired. */
const MAX_REFRESH_ATTEMPTS = 3;

/** All auth-endpoint paths — 401s on these are never retried (avoids loops). */
const AUTH_BASE_PATH = '/auth/';

/** Login path — uses Basic Auth only, so Bearer is never injected here. */
const LOGIN_PATH = '/auth/token';


/**
 * In-flight refresh Observable shared across concurrent 401 callers.
 * Null when no refresh is underway. Reset to null immediately before the
 * {@link ReplaySubject} completes or errors so the next 401 round starts
 * a fresh refresh rather than replaying a stale result.
 */
let refreshInFlight$: Observable<void> | null = null;

/**
 * Returns the in-flight refresh Observable, starting one if none is active.
 *
 * @remarks
 * Uses a {@link ReplaySubject}(1) so subscribers that arrive after the
 * refresh Promise resolves still receive the result. Only a single
 * `AuthService.refresh()` call is made per round regardless of how many
 * requests 401 simultaneously — all concurrent callers subscribe to the
 * same subject.
 *
 * `refreshInFlight$` is reset to `null` *before* the subject emits so
 * that a recursive retry in `refreshAndRetry` (attempt + 1) starts a new
 * refresh rather than reusing the just-completed one.
 *
 * @param auth - {@link AuthService} whose `refresh()` is called.
 */
function getOrStartRefresh(auth: AuthService): Observable<void> {
  if (!refreshInFlight$) {
    const subject = new ReplaySubject<void>(1);
    refreshInFlight$ = subject.asObservable();
    auth.refresh().then(
      () => { refreshInFlight$ = null; subject.next(); subject.complete(); },
      (err: unknown) => { refreshInFlight$ = null; subject.error(err); },
    );
  }
  return refreshInFlight$;
}

/**
 * Attaches `Authorization: Bearer <token>` to every outgoing HTTP request
 * when the user is authenticated. Skips the login endpoint (Basic Auth only).
 *
 * @remarks
 * **Cache-control** — all GET requests receive `Cache-Control: no-cache` and
 * `Pragma: no-cache` so the browser always revalidates with the server rather
 * than serving a stale cached response. This prevents the Sign Queue (and
 * other live-data views) from displaying outdated server state after an admin
 * action (e.g. resetting a job's e-sign status) even when the user taps the
 * in-app Refresh icon. POST/PUT/DELETE requests are never cached by browsers
 * and therefore do not receive these headers.
 *
 * **401 handling** — on a 401 from a non-auth endpoint, calls
 * {@link getOrStartRefresh} (which deduplicates concurrent refresh calls)
 * and retries the original request with the new token. Retries up to
 * {@link MAX_REFRESH_ATTEMPTS} times; on exhaustion calls
 * {@link AuthService.clearSession}, shows a "Session expired" toast,
 * and navigates to `/auth`.
 *
 * **Error toasts** — non-401, non-auth errors are surfaced by
 * `globalErrorHandlerInterceptor` (registered after this interceptor in the
 * chain). Auth-endpoint errors (wrong password, expired refresh token) are not
 * toasted at all — the calling component is responsible for those messages.
 *
 * All endpoints except `/auth/token` receive the Bearer header automatically
 * so services do not build it manually.
 */
/**
 * Checks whether a URL is same-origin (relative or same domain as the current location).
 * Returns true for relative URLs and false for cross-origin absolute URLs.
 */
function isSameOriginRequest(url: string): boolean {
  // Relative URLs are always same-origin
  if (url.startsWith('/')) return true;

  // Absolute URLs: check if the origin matches
  try {
    const reqUrl = new URL(url, window.location.origin);
    return reqUrl.origin === window.location.origin;
  } catch {
    // If URL parsing fails, assume same-origin to be safe
    return true;
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const token = auth.token();
  const isAuthPath = req.url.includes(AUTH_BASE_PATH);

  const isGet = req.method === 'GET';
  const isSameOrigin = isSameOriginRequest(req.url);

  // Build request with appropriate headers
  let outbound = req;

  if (token && !req.url.includes(LOGIN_PATH)) {
    // Authenticated request
    const headers: { [key: string]: string } = { Authorization: `Bearer ${token.accessToken}` };
    if (isGet && isSameOrigin) {
      headers['Cache-Control'] = 'no-cache';
      headers['Pragma'] = 'no-cache';
    }
    outbound = req.clone({ setHeaders: headers });
  } else if (isGet && isSameOrigin) {
    // Unauthenticated GET request to same-origin
    outbound = req.clone({ setHeaders: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });
  }

  return next(outbound).pipe(
    catchError((err: HttpErrorResponse) => {
      // A request already in flight when the user tapped "Log out" can come
      // back 401 shortly after (logout() invalidates the token server-side
      // — see AuthService.manualLogoutInProgress's doc comment) — that must
      // never be treated as an involuntary session expiration on top of an
      // already-in-progress, perfectly normal logout.
      if (err.status === 401 && !isAuthPath && !auth.manualLogoutInProgress()) {
        return refreshAndRetry(req, next, auth, router, 0);
      }
      return throwError(() => err);
    }),
  );
};

/**
 * Attempts to refresh the access token and retry the original request.
 * Recurses up to {@link MAX_REFRESH_ATTEMPTS} times on any 401.
 * On exhaustion, calls {@link AuthService.clearSession} (which emits
 * {@link AuthService.sessionExpired$}) and navigates to `/auth`.
 * `LoginComponent` handles the "Session expired" toast once rendered.
 *
 * @param original - Original request that triggered the 401.
 * @param next     - Next handler in the interceptor chain.
 * @param auth     - {@link AuthService} for token operations.
 * @param router   - Angular {@link Router} for post-expiry navigation.
 * @param attempt  - Zero-based attempt counter.
 */
function refreshAndRetry(
  original: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  router: Router,
  attempt: number,
): Observable<HttpEvent<unknown>> {
  if (attempt >= MAX_REFRESH_ATTEMPTS || !auth.token()) {
    void auth.clearSession();
    void router.navigate(['/auth'], { replaceUrl: true });
    return throwError(() => new Error('Session expired'));
  }

  return getOrStartRefresh(auth).pipe(
    switchMap(() => {
      const t = auth.token();
      const retried = t
        ? original.clone({ setHeaders: { Authorization: `Bearer ${t.accessToken}` } })
        : original;
      return next(retried);
    }),
    catchError((err: unknown) => {
      const is401 = err instanceof HttpErrorResponse && err.status === 401;
      if (!is401) return throwError(() => err);
      return refreshAndRetry(original, next, auth, router, attempt + 1);
    }),
  );
}